import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface PermittedTask {
  id: string;
  name: string;
  slug: string;
  displayOrder: number;
}

export interface UserGroupProfile {
  id: string;
  name: string;
}

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  fullName: string;
  roleId?: string;
  roleName?: string;
  groupId?: string;
  groupName?: string;
  groups?: UserGroupProfile[];
  facilityId?: string;
  facilityName?: string;
  isActive: boolean;
  isRemote: boolean;
  permittedTasks?: PermittedTask[];
  permissions?: string[];
}

interface AuthState {
  token: string | null;
  profile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setToken: (token: string | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  login: (token: string, profile: UserProfile) => void;
  logout: () => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      profile: null,
      isLoading: true,
      isAuthenticated: false,
      setToken: (token) => set({ token, isAuthenticated: !!token }),
      setProfile: (profile) => set({ profile }),
      setLoading: (isLoading) => set({ isLoading }),
      login: (token, profile) =>
        set({
          token,
          profile,
          isAuthenticated: true,
          isLoading: false,
        }),
      logout: () => {
        localStorage.removeItem("token");
        // Dynamically import to avoid circular dependency
        import("./task.store").then(({ useTaskStore }) => {
          useTaskStore.getState().clearTaskSelection();
        });
        set({
          token: null,
          profile: null,
          isAuthenticated: false,
        });
      },
      clearAuth: () =>
        set({
          token: null,
          profile: null,
          isAuthenticated: false,
        }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        profile: state.profile,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
